import type { i18n } from "i18next";
import { isObject, unset } from "lodash";

import { I18N_DEFAULT_NAMESPACE, LANGS_SUPPORTED } from "../constants";
import type { ICustomTranslation, SupportedLanguage } from "../models";

interface IAppliedOverride {
  language: SupportedLanguage;
  path: string;
  original: unknown;
  value: string;
}

// The company's overrides, kept so a language loaded later (a language switch) gets them too.
let companyTranslations: ICustomTranslation[] = [];
// What was written into the catalogue and what it replaced, so another company's overrides do not stay behind.
let appliedOverrides: IAppliedOverride[] = [];
const instancesWithLanguageListener = new WeakSet<i18n>();
// Company selections can overlap; only the latest one may write its overrides and its scope.
let lastLoadId = 0;

const revertAppliedOverrides = (i18nInstance: i18n): void => {
  appliedOverrides.forEach(({ language, path, original, value }) => {
    // A catalogue reloaded since (logout and login) no longer holds the override.
    if (
      i18nInstance.getResource(language, I18N_DEFAULT_NAMESPACE, path) !== value
    ) {
      return;
    }

    if (typeof original !== "string") {
      unset(i18nInstance.store.data, [
        language,
        I18N_DEFAULT_NAMESPACE,
        ...path.split("."),
      ]);
      return;
    }

    i18nInstance.addResource(language, I18N_DEFAULT_NAMESPACE, path, original, {
      silent: true,
    });
  });
  appliedOverrides = [];
};

/**
 * Only into a language whose full catalogue is in memory: overrides merged into a missing one would stand in for
 * it, and i18next would not load it again.
 */
const applyCompanyTranslations = (i18nInstance: i18n): void => {
  revertAppliedOverrides(i18nInstance);

  const languages = LANGS_SUPPORTED.filter(language =>
    i18nInstance.hasResourceBundle(language, I18N_DEFAULT_NAMESPACE),
  );

  languages.forEach(language => {
    companyTranslations.forEach(translation => {
      const value = translation[language];

      if (!translation.path || !value) {
        return;
      }

      const original: unknown = i18nInstance.getResource(
        language,
        I18N_DEFAULT_NAMESPACE,
        translation.path,
      );

      // A path to a whole section would replace every key under it with one string.
      if (isObject(original)) {
        return;
      }

      appliedOverrides.push({
        language,
        path: translation.path,
        original,
        value,
      });
      i18nInstance.addResource(
        language,
        I18N_DEFAULT_NAMESPACE,
        translation.path,
        value,
        { silent: true },
      );
    });
  });
};

/**
 * Replace the overrides of the previous company with `translations`. Call it once the full catalogue has loaded;
 * a language that loads afterwards gets them when it becomes the current one.
 */
export const setCompanyCustomTranslations = ({
  i18nInstance,
  translations,
}: {
  i18nInstance: i18n;
  translations: ICustomTranslation[];
}): void => {
  companyTranslations = translations;
  applyCompanyTranslations(i18nInstance);

  if (!instancesWithLanguageListener.has(i18nInstance)) {
    instancesWithLanguageListener.add(i18nInstance);
    i18nInstance.on("languageChanged", () =>
      applyCompanyTranslations(i18nInstance),
    );
  }
};

/** Start loading a company's overrides; a load started after it makes it stale. */
export const startCompanyCustomTranslationsLoad = (): number => {
  lastLoadId += 1;
  return lastLoadId;
};

/** Whether no company selection or logout has happened since `loadId` started. */
export const isCurrentCompanyCustomTranslationsLoad = (
  loadId: number,
): boolean => loadId === lastLoadId;

/** Drop the company's overrides from the catalogue, e.g. on logout, so the next session does not inherit them. */
export const clearCompanyCustomTranslations = (i18nInstance: i18n): void => {
  lastLoadId += 1;
  companyTranslations = [];
  revertAppliedOverrides(i18nInstance);
};
